import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import {
  MatTable,
  MatTableDataSource,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { forkJoin } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { UtilsService } from 'src/app/services/utils.service';
import { SelectorDialogComponent, SelectorDialogDataRow } from 'src/app/components/main/shared/selector-dialog/selector-dialog.component';
import { Company } from 'src/app/models/company.model';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { SlackWorkspace } from 'src/app/models/slack-workspace.model';
import { MicrosoftTenant } from 'src/app/models/microsoft-tenant.model';
import { GoogleChatCustomer } from 'src/app/models/google-chat-customer.model';

type WorkspaceProvider = 'slack' | 'teams' | 'google_chat';
type WorkspaceStatusFilter = 'pending' | 'ignored' | 'all';

interface WorkspaceRow {
  id: number;
  provider: WorkspaceProvider;
  provider_label: string;
  name: string;
  external_id: string;
  company_id: number | null;
  ignored_for_assignment: boolean;
  status_label: string;
  is_pending: boolean;
  created_at: string | null;
}

@Component({
  selector: 'app-messaging-workspaces',
  templateUrl: './messaging-workspaces.component.html',
  styleUrl: './messaging-workspaces.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatIconButton,
    MatIcon,
    MatPaginator,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
})
export class MessagingWorkspacesComponent extends IndexTemplateComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly change_detector_ref = inject(ChangeDetectorRef);

  displayedColumns: string[] = ['provider', 'name', 'external_id', 'status', 'actions'];
  dataSource = new MatTableDataSource<WorkspaceRow>([]);

  override filterFormExtraParams: Record<string, unknown> = {
    provider: [''],
    status: ['pending'],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.fetchData();
  }

  override fetchData(): void {
    const provider = String(this.filterForm.get('provider')?.value ?? '');
    const status = this.resolveStatusFilter();
    const list_params = this.buildListParams(status);

    forkJoin({
      slack: this.api.getSlackWorkspacesPaged(list_params),
      teams: this.api.getMicrosoftTenantsPaged(list_params),
      google: this.api.getGoogleChatCustomersPaged(list_params),
    }).subscribe({
      next: ({ slack, teams, google }): void => {
        const rows: WorkspaceRow[] = [
          ...this.mapSlack(slack.data ?? []),
          ...this.mapTeams(teams.data ?? []),
          ...this.mapGoogle(google.data ?? []),
        ]
          .filter((row: WorkspaceRow): boolean => provider === '' || row.provider === provider)
          .sort((a: WorkspaceRow, b: WorkspaceRow): number => {
            const a_ts = a.created_at ? Date.parse(a.created_at) : 0;
            const b_ts = b.created_at ? Date.parse(b.created_at) : 0;

            return b_ts - a_ts;
          });

        this.dataSource.data = rows;
        this.setMetadata(rows.length, 0, rows.length || 25);
        this.change_detector_ref.markForCheck();
      },
    });
  }

  assignCompany(row: WorkspaceRow): void {
    this.api.getCompanies({ search: '', per_page: 50 }).subscribe({
      next: (response: ArrayResponse<Company>): void => {
        const data: SelectorDialogDataRow[] = (response.data ?? []).map(
          (company: Company): SelectorDialogDataRow => ({
            value: company.id,
            textToShow: company.fact_name,
          })
        );

        this.dialog
          .open(SelectorDialogComponent, {
            data: {
              dialog_title: 'Asociar empresa',
              dialog_ok_button: 'Asociar',
              dialog_cancel_button: 'Cancelar',
              data,
            },
          })
          .afterClosed()
          .subscribe((company_id: number | undefined): void => {
            if (company_id == null) {
              return;
            }

            this.linkWorkspace(row, Number(company_id));
          });
      },
    });
  }

  ignore(row: WorkspaceRow): void {
    const request =
      row.provider === 'slack'
        ? this.api.ignoreSlackWorkspace(row.id)
        : row.provider === 'teams'
          ? this.api.ignoreMicrosoftTenant(row.id)
          : this.api.ignoreGoogleChatCustomer(row.id);

    request.subscribe({
      next: (): void => this.fetchData(),
    });
  }

  private linkWorkspace(row: WorkspaceRow, company_id: number): void {
    const request =
      row.provider === 'slack'
        ? this.api.linkSlackWorkspace(row.id, { company_id })
        : row.provider === 'teams'
          ? this.api.linkMicrosoftTenant(row.id, { company_id })
          : this.api.linkGoogleChatCustomer(row.id, { company_id });

    request.subscribe({
      next: (): void => this.fetchData(),
    });
  }

  private resolveStatusFilter(): WorkspaceStatusFilter {
    const status = String(this.filterForm.get('status')?.value ?? 'pending');

    if (status === 'ignored' || status === 'all') {
      return status;
    }

    return 'pending';
  }

  private buildListParams(status: WorkspaceStatusFilter): Record<string, unknown> {
    const params: Record<string, unknown> = {
      page: 1,
      per_page: 200,
      order_by: 'created_at:desc',
      with: 'company',
    };

    if (status === 'pending') {
      params['scopes'] = 'pendingAssignment';
    } else if (status === 'ignored') {
      params['ignored_for_assignment'] = true;
    }

    return params;
  }

  private mapSlack(items: SlackWorkspace[]): WorkspaceRow[] {
    return items.map(
      (item: SlackWorkspace): WorkspaceRow =>
        this.toWorkspaceRow({
          id: item.id,
          provider: 'slack',
          provider_label: 'Slack',
          name: item.name,
          external_id: item.team_id,
          company_id: item.company_id,
          ignored_for_assignment: item.ignored_for_assignment,
          company_name: item.company?.fact_name ?? null,
          created_at: item.created_at ?? null,
        })
    );
  }

  private mapTeams(items: MicrosoftTenant[]): WorkspaceRow[] {
    return items.map(
      (item: MicrosoftTenant): WorkspaceRow =>
        this.toWorkspaceRow({
          id: item.id,
          provider: 'teams',
          provider_label: 'Teams',
          name: item.name ?? item.tenant_id,
          external_id: item.tenant_id,
          company_id: item.company_id,
          ignored_for_assignment: item.ignored_for_assignment,
          company_name: item.company?.fact_name ?? null,
          created_at: item.created_at ?? null,
        })
    );
  }

  private mapGoogle(items: GoogleChatCustomer[]): WorkspaceRow[] {
    return items.map(
      (item: GoogleChatCustomer): WorkspaceRow =>
        this.toWorkspaceRow({
          id: item.id,
          provider: 'google_chat',
          provider_label: 'Google Chat',
          name: item.display_name ?? item.customer_id ?? item.domain_id ?? `#${item.id}`,
          external_id: item.customer_id ?? item.domain_id ?? '',
          company_id: item.company_id,
          ignored_for_assignment: item.ignored_for_assignment,
          company_name: item.company?.fact_name ?? null,
          created_at: item.created_at ?? null,
        })
    );
  }

  private toWorkspaceRow(input: {
    id: number;
    provider: WorkspaceProvider;
    provider_label: string;
    name: string;
    external_id: string;
    company_id: number | null;
    ignored_for_assignment: boolean;
    company_name: string | null;
    created_at: string | null;
  }): WorkspaceRow {
    const is_pending = input.company_id == null && !input.ignored_for_assignment;

    let status_label = 'Pendiente de empresa';
    if (input.ignored_for_assignment) {
      status_label = 'Omitido';
    } else if (input.company_id != null) {
      status_label = input.company_name ?? `Empresa #${input.company_id}`;
    }

    return {
      id: input.id,
      provider: input.provider,
      provider_label: input.provider_label,
      name: input.name,
      external_id: input.external_id,
      company_id: input.company_id,
      ignored_for_assignment: input.ignored_for_assignment,
      status_label,
      is_pending,
      created_at: input.created_at,
    };
  }
}
